// src/views/Admin/DatosPlayeras.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import {
  faHeartbeat,
  faTemperatureHigh,
  faTint,
  faChair,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from '../../context/AuthContext';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Tooltip,
  Legend
);

export default function DatosPlayeras() {
  const { user } = useAuth();
  const [signos, setSignos] = useState([]);

  useEffect(() => {
    const fetchGlobalSigns = async () => {
      try {
        const res = await axios.get('/api/admin/signosvitales');
        setSignos(res.data.reverse()); 
      } catch (err) {
        console.error('Error al obtener datos globales:', err);
      }
    };

    fetchGlobalSigns();
    const interval = setInterval(fetchGlobalSigns, 5000);
    return () => clearInterval(interval);
  }, []);

  // Preparar labels (timestamps para el eje X)
  const labels = signos.map((d) =>
    new Date(d.timestamp).toLocaleTimeString('es-MX')
  );

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: { y: { beginAtZero: true } },
    plugins: { legend: { display: false } },
  };

  const ritmoData = {
    labels,
    datasets: [
      {
        label: 'Ritmo Cardiaco (ppm)',
        data: signos.map((d) => d.ritmo_cardiaco),
        borderColor: 'rgb(220, 38, 38)', // rojo
        backgroundColor: 'rgba(220, 38, 38, 0.3)',
        fill: true,
        tension: 0.3,
      },
    ],
  };

  const tempData = {
    labels,
    datasets: [
      {
        label: 'Temperatura (°C)',
        data: signos.map((d) => d.temp_corporal),
        backgroundColor: 'rgba(59, 130, 246, 0.7)', // azul
        borderColor: 'rgb(59, 130, 246)',
      },
    ],
  };

  const spo2Data = {
    labels,
    datasets: [
      {
        label: 'SpO2 (%)',
        data: signos.map((d) => d.spo2),
        backgroundColor: 'rgba(5, 150, 105, 0.7)', // verde
        borderColor: 'rgb(5, 150, 105)',
      },
    ],
  };

  const posturaCount = signos.reduce(
    (acc, curr) => {
      if (curr.postura === 'mala') acc.mala++;
      else acc.buena++;
      return acc;
    },
    { buena: 0, mala: 0 }
  );

  const posturaData = {
    labels: ['Buena', 'Mala'],
    datasets: [
      {
        label: 'Postura',
        data: [posturaCount.buena, posturaCount.mala],
        backgroundColor: ['#22c55e', '#991b1b'], // verde éxito / rojo oscuro
        borderRadius: 4,
      },
    ],
  };

  return (
    <div className="max-w-[1200px] mx-auto p-8">
  <h1 className="text-2xl font-bold text-gray-800 mb-6">
    Monitor Global de Signos Vitales
  </h1>

  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    {/* Ritmo Cardíaco */}
    <div className="bg-white/80 p-4 rounded-xl shadow-md">
      <div className="flex items-center gap-2 mb-2 text-red-600 font-semibold text-md">
        <FontAwesomeIcon icon={faHeartbeat} />
        <span>Ritmo Cardíaco</span>
      </div>
      <div className="h-64">
        <Line data={ritmoData} options={options} />
      </div>
    </div>

    {/* Temperatura */}
    <div className="bg-white/80 p-4 rounded-xl shadow-md">
      <div className="flex items-center gap-2 mb-2 text-blue-600 font-semibold text-md">
        <FontAwesomeIcon icon={faTemperatureHigh} />
        <span>Temperatura</span>
      </div>
      <div className="h-64">
        <Bar data={tempData} options={options} />
      </div>
    </div>

    {/* SpO2 */}
    <div className="bg-white/80 p-4 rounded-xl shadow-md">
      <div className="flex items-center gap-2 mb-2 text-teal-600 font-semibold text-md">
        <FontAwesomeIcon icon={faTint} />
        <span>Oxígeno en Sangre</span>
      </div>
      <div className="h-64">
        <Bar data={spo2Data} options={options} />
      </div>
    </div>

    {/* Postura */}
    <div className="bg-white/80 p-4 rounded-xl shadow-md">
      <div className="flex items-center gap-2 mb-2 text-purple-600 font-semibold text-md">
        <FontAwesomeIcon icon={faChair} />
        <span>Postura</span>
      </div>
      <div className="h-64">
        <Bar data={posturaData} options={options} />
      </div>
    </div>
  </div>
</div>

  );
}
